/**
 * BookFileStore - the downloaded book PDFs, kept encrypted on the device (ADR 0018).
 *
 * Each row holds the AES-GCM ciphertext, its IV and a non-extractable CryptoKey,
 * keyed by `${uid}:${pdfId}`. The key cannot be exported, so the stored bytes are
 * only usable inside this browser profile.
 */
import Dexie, { type Table } from 'dexie';
import { HttpError, PdfApiService } from '$lib/services/ApiService';

export interface StoredBook {
  uid: string;
  pdfId: string;
  version: string;
  iv: Uint8Array<ArrayBuffer>;
  data: ArrayBuffer;
  key: CryptoKey;
}

class BookFileDB extends Dexie {
  books!: Table<StoredBook, string>;

  constructor() {
    super('book-files');
    this.version(1).stores({ books: ',uid' });
  }
}

const db = new BookFileDB();
const rowKey = (uid: string, pdfId: string) => `${uid}:${pdfId}`;

export async function encryptBook(bytes: ArrayBuffer): Promise<Pick<StoredBook, 'iv' | 'data' | 'key'>> {
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, bytes);
  return { iv, data, key };
}

export function decryptBook(row: Pick<StoredBook, 'iv' | 'data' | 'key'>): Promise<ArrayBuffer> {
  return crypto.subtle.decrypt({ name: 'AES-GCM', iv: row.iv }, row.key, row.data);
}

/**
 * A complete PDF starts with `%PDF-` and ends with `%%EOF` (maybe followed by a
 * few bytes of whitespace). A cut-off download fails this and is never stored.
 */
export function isCompletePdf(bytes: ArrayBuffer): boolean {
  const view = new Uint8Array(bytes);
  if (view.length < 16) return false;
  const ascii = (from: number, to: number) => String.fromCharCode(...view.subarray(from, to));
  return ascii(0, 5) === '%PDF-' && ascii(Math.max(0, view.length - 1024), view.length).includes('%%EOF');
}

/** Whether a copy of the book is stored for this user (does not read the ciphertext). */
export async function hasBook(uid: string, pdfId: string): Promise<boolean> {
  return (await db.books.where(':id').equals(rowKey(uid, pdfId)).count()) > 0;
}

/** Deletes all stored books of one user (on logout). */
export async function clearUserBooks(uid: string): Promise<void> {
  await db.books.where('uid').equals(uid).delete();
}

/** A failing IndexedDB (private mode, blocked) counts as "nothing stored". */
const getRow = (id: string) => db.books.get(id).catch(() => undefined);

/** Thrown by `openBook` when the user declined a download and there is no copy on the device. */
export class DownloadDeclinedError extends Error {
  constructor() {
    super('Book download declined');
  }
}

// A download ticket is valid for 60 s; one older than this is replaced after the user confirms.
const TICKET_REUSE_MS = 40_000;

export interface OpenBookOptions {
  /** Bytes received so far and the expected size, while downloading. */
  onProgress?: (loaded: number, total: number) => void;
  /** Fires when a download starts: the server first stamps the book (no bytes yet). */
  onRequest?: () => void;
  /**
   * Asked before every download (the books are 50+ MB; the user may be on a metered
   * connection). `update` is true when an older version is on the device: declining then
   * opens that copy. Resolve false to decline. Without it, downloads start right away.
   */
  confirmDownload?: (info: { bytes: number; update: boolean }) => Promise<boolean>;
}

/**
 * Returns the book's plaintext PDF. Uses the stored copy when its version matches the
 * server's; otherwise (after `confirmDownload`, when given) downloads once and stores it
 * encrypted. Offline (token call fails, except 403) any stored copy is used. A 403 deletes
 * the stored copy and rethrows.
 */
export async function openBook(
  uid: string,
  pdfId: string,
  { onProgress, onRequest, confirmDownload }: OpenBookOptions = {},
): Promise<ArrayBuffer> {
  const id = rowKey(uid, pdfId);
  let token: Awaited<ReturnType<typeof PdfApiService.getPdfDownloadToken>>;
  try {
    token = await PdfApiService.getPdfDownloadToken(pdfId);
  } catch (e) {
    if (e instanceof HttpError && e.status === 403) {
      await db.books.delete(id).catch((err) => console.error('Failed to delete stored book:', err));
      throw e;
    }
    const row = await getRow(id);
    if (row) return decryptBook(row);
    throw e;
  }
  let tokenAt = Date.now();

  let row = await getRow(id);
  if (row?.version === token.version) {
    const stored = await decryptBook(row);
    if (isCompletePdf(stored)) return stored;
    // A copy saved from a cut-off download: drop it and download again.
    await db.books.delete(id).catch((err) => console.error('Failed to delete stored book:', err));
    row = undefined;
  }

  if (confirmDownload) {
    const old = row ? await decryptBook(row).catch(() => null) : null;
    const usableOld = old && isCompletePdf(old) ? old : null;
    if (!(await confirmDownload({ bytes: token.fileSize, update: !!usableOld }))) {
      if (usableOld) return usableOld;
      throw new DownloadDeclinedError();
    }
    if (Date.now() - tokenAt > TICKET_REUSE_MS) {
      token = await PdfApiService.getPdfDownloadToken(pdfId);
      tokenAt = Date.now();
    }
  }

  onRequest?.();
  const bytes = await PdfApiService.downloadPdf(token.ticket, (loaded) => onProgress?.(loaded, token.fileSize));
  if (!isCompletePdf(bytes)) throw new Error(`Incomplete book download (${bytes.byteLength} bytes)`);
  try {
    await db.books.put({ uid, pdfId, version: token.version, ...(await encryptBook(bytes)) }, id);
  } catch (e) {
    // Storage full or unavailable: the book still opens, it is just downloaded again next time.
    console.error('Failed to store book on device:', e);
  }
  return bytes;
}
